import {
  formatDateOnly,
  formatDateWithTime,
} from '../../promotions/view-model/utils';
import { EmptyObject, F1 } from '@client/new/helpers/types';
import {
  CreateChallengePayload,
  LiteChallenge,
  PoseGuide,
  UpdateChallengePayload,
} from '../model/Challenges.types';
import { ChallengeForm } from './ChallengeForm.schema';
import { createDate, DateTime } from './utils';
import { toDateTimeSchema } from './utils';
import { ChallengesModel } from '../model/Challenges.model';
import { ChallengesDispatchers, ChallengeVM } from './Challenges.viewModel';
import {
  button,
  ContentType,
  headerCol,
  headerColValue,
  link,
  pill,
  row,
  RowType,
  text,
} from '../view/components/common/ContentTable';
import { Sorting, SortingOrder } from '../view/components/common/useSorting';
import { challengeStatusColors } from '../view/components/ChallengeElement';

type WithRole<T extends EmptyObject> = T & { role: 'editable' | 'viewable' };

const toModeCells = (c: WithRole<LiteChallenge>) => {
  const status = toChallengeStatus(c);
  return [
    text(formatDateOnly(new Date(c.createdAt))),
    text(formatDateOnly(c.registration.data[0])),
    text(formatDateOnly(c.completion.data[0])),
    pill({
      content: `${status}`,
      backgroundColor: challengeStatusColors[status],
    }),
    text(c.submissionsCount.toString()),
  ];
};

const toChallengeRow = (
  c: WithRole<LiteChallenge & { onSelectChallenge: F1<string> }>,
): RowType => {
  const cells: Array<ContentType> = [
    ...(c.name ? [text(c.name)] : []),
    ...toModeCells(c),
    button({
      content: c.role === 'viewable' ? 'View' : 'Edit',
      onClick: () => c.onSelectChallenge(c.challengeId),
    }),
    link({
      to: c.submissionsURL,
      content: `Submissions`,
    }),
  ];
  return row({
    id: c.challengeId,
    cells,
  });
};

const getValue = (key: SortableColumn, c: LiteChallenge) => {
  if (key === 'name') return c.name;
  if (key === 'dateCreated') return c.createdAt;
  if (key === 'registration') return c.registration.data[0];
  if (key === 'completion') return c.completion.data[0];
  if (key === 'submissions') return c.submissionsCount;
  if (key === 'status') return toChallengeStatus(c);
  return null;
};

const withOrder = (order: SortingOrder) => (value: boolean) =>
  value ? (order === 'up' ? 1 : -1) : order === 'up' ? -1 : 1;
const compare =
  (key: SortableColumn, order: SortingOrder) =>
  (c1: LiteChallenge, c2: LiteChallenge) => {
    const l = getValue(key, c1);
    const r = getValue(key, c2);
    const toRes = withOrder(order);

    if (typeof l === 'number') return toRes((l as number) > (r as number));
    if (l instanceof Date && r instanceof Date)
      return toRes(l.getTime() > r.getTime());
    if (key === 'status') {
      const ls = timeStatuses.indexOf(l as TimeStatus);
      const rs = timeStatuses.indexOf(r as TimeStatus);
      return ls === rs ? 0 : toRes(ls > rs);
    }
    if (typeof l === 'string')
      return toRes((l as string).localeCompare(r as string) >= 0);
    return 0;
  };

export const toChallengesList = ({
  cs,
  ...p
}: WithRole<{
  sorting: Sorting<SortableColumn>;
  cs: Array<LiteChallenge>;
  onSelectChallenge: (cid: string) => void;
}>) => {
  const hasKey = Boolean(p.sorting.column);
  const hasOrder = Boolean(p.sorting.order);
  if (hasKey && hasOrder) {
    cs.sort(
      compare(
        p.sorting.column as SortableColumn,
        p.sorting.order as SortingOrder,
      ),
    );
  }
  return cs.map((c) => toChallengeRow({ ...c, ...p }));
};

const toChallengePayloadBase = (c: ChallengeForm) => ({
  name: c.name || '',
  completionEnd: createDate(c.completionEnd as DateTime),
  completionStart: createDate(c.completionStart as DateTime),
  registrationEnd: createDate(c.registrationEnd as DateTime),
  registrationStart: createDate(c.registrationStart as DateTime),
  hidePhotos: !c.showPhotosSection,
  registrationRequirements: {
    goal: c.submissionRequirements.goal || false,
    instagram: c.submissionRequirements.instagram || false,
    phoneNumber: c.submissionRequirements.phoneNumber || false,
    photos: c.submissionRequirements.photos || false,
  },
  completionRequirements: {
    testimonial: c.completionRequirements.testimonial || false,
    photos: c.completionRequirements.photos || false,
  },
  templates: c.templates,
  photoInstructions: c.photoInstructions || '',
  photoGuide: (c.photoGuide || []) as PoseGuide[],
});

export const toCreateChallengePayload = (
  c: ChallengeForm & {
    programId: number;
  },
): CreateChallengePayload => {
  return {
    ...toChallengePayloadBase(c),
    programId: c.programId,
  };
};

export const toUpdateChallengePayload = (
  c: ChallengeForm & { challengeId: string },
): UpdateChallengePayload => {
  return {
    id: parseInt(c.challengeId, 10),
    ...toChallengePayloadBase(c),
  };
};

export const toChallengeForm = (c: LiteChallenge): ChallengeForm => {
  return {
    name: c.name || '',
    registrationStart: toDateTimeSchema(c.registration.data[0]),
    registrationEnd: toDateTimeSchema(c.registration.data[1]),
    completionStart: toDateTimeSchema(c.completion.data[0]),
    completionEnd: toDateTimeSchema(c.completion.data[1]),
    showPhotosSection: !c.hidePhotos,
    submissionRequirements: {
      instagram: c.registration.instagram,
      goal: c.registration.goal,
      phoneNumber: c.registration.phoneNumber,
      photos: c.registration.photos || false,
    },
    completionRequirements: {
      testimonial: c.completion.testimonial,
      photos: c.completion.photos,
    },
    photoInstructions: c.photoInstructions,
    photoGuide: c.photoGuide,
    templates: c.templates,
  };
};
const timeStatuses = ['Scheduled', 'Ongoing', 'Finished'] as const;
export type TimeStatus = (typeof timeStatuses)[number];
const toTimeStatus = (d: [Date, Date]): TimeStatus => {
  const now = new Date();
  if (now < d[0]) return 'Scheduled';
  if (now > d[1]) return 'Finished';
  return 'Ongoing';
};

const toChallengePartDate = (d: [Date, Date]) => {
  const status = toTimeStatus(d);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const toDate = (date: Date, prefix: string) =>
    formatDateWithTime(date, prefix, tz);

  if (status === 'Scheduled') return { status, desc: toDate(d[0], 'Opens ') };
  if (status === 'Finished') return { status, desc: toDate(d[1], 'Closed ') };
  return { status, desc: toDate(d[1], 'Until ') };
};

export const toChallengeStatus = (c: LiteChallenge): TimeStatus => {
  const now = new Date();

  const registrationStart = new Date(c.registration.data[0]);
  const registrationEnd = new Date(c.registration.data[1]);
  const completionStart = new Date(c.completion.data[0]);
  const completionEnd = new Date(c.completion.data[1]);

  if (now < registrationStart) return 'Scheduled';
  if (now >= registrationStart && now <= registrationEnd) return 'Ongoing';
  if (now > registrationEnd && now < completionStart) return 'Scheduled';
  if (now >= completionStart && now <= completionEnd) return 'Ongoing';

  return 'Finished';
};

export const toChallengeVM = (
  m: ChallengesModel,
  { dispatch }: ChallengesDispatchers,
): ChallengeVM | null => {
  if (m.challenges.length === 0) return null;
  const cs = m.challenges.map((c) => ({ ...c, status: toChallengeStatus(c) }));
  const c =
    cs.find((c) => c.status === 'Ongoing') ||
    cs.find((c) => c.status === 'Scheduled') ||
    cs[0];

  const res: ChallengeVM = {
    name: c.name || 'Ongoing challenge',
    submissionsCount: c.submissionsCount,
    challengeId: c.challengeId,
    status: c.status,
    submissionURL: c.submissionsURL,
    registration: toChallengePartDate(c.registration.data),
    completion: toChallengePartDate(c.completion.data),
  };

  res.onEdit = () => dispatch('EditChallenge', c.challengeId);
  res.onNewChallenge = () => dispatch('CreateChallenge');

  return res;
};

export const sortableColumnsMap = {
  name: headerColValue({ content: 'Name', width: '1fr', align: 'left' }),
  dateCreated: headerColValue({ content: 'Date created' }),
  registration: headerColValue({
    name: 'registration',
    content: 'Registration',
  }),
  completion: headerColValue({ content: 'Completion' }),
  submissions: headerColValue({ content: 'Submissions' }),
  status: headerColValue({ content: 'Status' }),
};
export type SortableColumn = keyof typeof sortableColumnsMap;

const toHeaderCol = (name: SortableColumn) =>
  headerCol({ name, ...sortableColumnsMap[name] });

export const challengesHeader = [
  toHeaderCol('name'),
  toHeaderCol('dateCreated'),
  toHeaderCol('registration'),
  toHeaderCol('completion'),
  toHeaderCol('status'),
  toHeaderCol('submissions'),
  headerCol({}),
  headerCol({}),
];
